import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { updateProjectSchema, type UpdateProjectInput } from '@everplan/shared';
import { client } from '@/lib/api';

interface EditProjectDialogProps {
  project: {
    id: string;
    name: string;
    person1Name: string | null;
    person1Email: string | null;
    person1Phone: string | null;
    person2Name: string | null;
    person2Email: string | null;
    person2Phone: string | null;
    weddingDate: string | null;
    weddingTime: string | null;
    guestCount: number | null;
    venueName: string | null;
    websiteUrl: string | null;
    pinterestUrl: string | null;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function EditProjectDialog({ project, open, onOpenChange }: EditProjectDialogProps) {
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<UpdateProjectInput>({
    resolver: zodResolver(updateProjectSchema),
    defaultValues: {
      name: project.name,
      person1Name: project.person1Name ?? undefined,
      person1Email: project.person1Email ?? undefined,
      person1Phone: project.person1Phone ?? undefined,
      person2Name: project.person2Name ?? undefined,
      person2Email: project.person2Email ?? undefined,
      person2Phone: project.person2Phone ?? undefined,
      weddingDate: project.weddingDate ?? undefined,
      weddingTime: project.weddingTime ?? undefined,
      guestCount: project.guestCount ?? undefined,
      venueName: project.venueName ?? undefined,
      websiteUrl: project.websiteUrl ?? undefined,
      pinterestUrl: project.pinterestUrl ?? undefined,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: project.name,
        person1Name: project.person1Name ?? undefined,
        person1Email: project.person1Email ?? undefined,
        person1Phone: project.person1Phone ?? undefined,
        person2Name: project.person2Name ?? undefined,
        person2Email: project.person2Email ?? undefined,
        person2Phone: project.person2Phone ?? undefined,
        weddingDate: project.weddingDate ?? undefined,
        weddingTime: project.weddingTime ?? undefined,
        guestCount: project.guestCount ?? undefined,
        venueName: project.venueName ?? undefined,
        websiteUrl: project.websiteUrl ?? undefined,
        pinterestUrl: project.pinterestUrl ?? undefined,
      });
    }
  }, [open, project, reset]);

  const mutation = useMutation({
    mutationFn: async (data: UpdateProjectInput) => {
      const res = await client.api.projects[':id'].$put({
        param: { id: project.id },
        json: data,
      });
      if (!res.ok) throw new Error('Failed to update project');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Project Details</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-6">
          {/* Project Info */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Project Info</h3>
            <div className="space-y-2">
              <label className="text-sm font-medium">Project Name</label>
              <Input {...register('name')} placeholder="Project name" />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">Wedding Date</label>
                <Input {...register('weddingDate')} type="date" />
                {errors.weddingDate && <p className="text-sm text-destructive">{errors.weddingDate.message}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Wedding Time</label>
                <Input {...register('weddingTime')} placeholder="e.g. 4:00 PM" />
                {errors.weddingTime && <p className="text-sm text-destructive">{errors.weddingTime.message}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">Venue</label>
                <Input {...register('venueName')} placeholder="Venue name" />
                {errors.venueName && <p className="text-sm text-destructive">{errors.venueName.message}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Guest Count</label>
                <Input
                  {...register('guestCount', { setValueAs: (v) => (v === '' ? undefined : Number(v)) })}
                  type="number"
                  min={1}
                  placeholder="e.g. 150"
                />
                {errors.guestCount && <p className="text-sm text-destructive">{errors.guestCount.message}</p>}
              </div>
            </div>
          </section>

          {/* Person 1 */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Person 1</h3>
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input {...register('person1Name')} placeholder="Full name" />
              {errors.person1Name && <p className="text-sm text-destructive">{errors.person1Name.message}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input {...register('person1Email')} type="email" placeholder="email@example.com" />
                {errors.person1Email && <p className="text-sm text-destructive">{errors.person1Email.message}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone</label>
                <Input {...register('person1Phone')} type="tel" placeholder="(555) 000-0000" />
                {errors.person1Phone && <p className="text-sm text-destructive">{errors.person1Phone.message}</p>}
              </div>
            </div>
          </section>

          {/* Person 2 */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Person 2</h3>
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input {...register('person2Name')} placeholder="Full name" />
              {errors.person2Name && <p className="text-sm text-destructive">{errors.person2Name.message}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input {...register('person2Email')} type="email" placeholder="email@example.com" />
                {errors.person2Email && <p className="text-sm text-destructive">{errors.person2Email.message}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone</label>
                <Input {...register('person2Phone')} type="tel" placeholder="(555) 000-0000" />
                {errors.person2Phone && <p className="text-sm text-destructive">{errors.person2Phone.message}</p>}
              </div>
            </div>
          </section>

          {/* Links */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Links</h3>
            <div className="space-y-2">
              <label className="text-sm font-medium">Website URL</label>
              <Input {...register('websiteUrl')} type="url" placeholder="https://ourwedding.com" />
              {errors.websiteUrl && <p className="text-sm text-destructive">{errors.websiteUrl.message}</p>}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Pinterest URL</label>
              <Input {...register('pinterestUrl')} type="url" placeholder="https://pinterest.com/..." />
              {errors.pinterestUrl && <p className="text-sm text-destructive">{errors.pinterestUrl.message}</p>}
            </div>
          </section>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
